"use client";

import { useEffect, useRef, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { sanitizeHtml } from "@/lib/sanitize-html";
import {
  replaceShortcodesWithPlaceholders,
  hasShortcodes,
  type ShortcodeData,
  type CarouselShortcodeData,
} from "@/lib/shortcode-parser";
import { Carousel } from "@/components/ui/carousel";

interface ShortcodeRendererProps {
  content: string;
  className?: string;
}

/**
 * Renders a carousel shortcode
 */
function CarouselShortcode({ data }: { data: CarouselShortcodeData }) {
  return (
    <Carousel
      images={data.images}
      autoplay={data.autoplay}
      interval={data.interval}
      showDots={data.showDots}
      showArrows={data.showArrows}
      aspectRatio={data.aspectRatio}
      height={data.height}
      width={data.width}
      fit={data.fit}
      slidesPerView={data.slidesPerView}
      gap={data.gap}
      loop={data.loop}
      focus={data.focus}
    />
  );
}

/**
 * ShortcodeRenderer Component
 *
 * Renders HTML content with embedded shortcodes using a placeholder + createRoot approach.
 */
export function ShortcodeRenderer({
  content,
  className,
}: ShortcodeRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rootsRef = useRef<Map<string, ReturnType<typeof createRoot>>>(new Map());

  // Parse content and create HTML with placeholders
  const html = useMemo(() => {
    if (!content) {
      return "";
    }

    // Quick check to avoid parsing if no shortcodes
    if (!hasShortcodes(content)) {
      return sanitizeHtml(content);
    }

    // Replace shortcodes with placeholders, then sanitize
    const result = replaceShortcodesWithPlaceholders(content);
    return sanitizeHtml(result.html);
  }, [content]);

  // After mount, find placeholder elements and render React components into them
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    // Capture ref value for cleanup
    const roots = rootsRef.current;

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const placeholderElements = containerRef.current?.querySelectorAll("[data-shortcode-config]");

      if (!placeholderElements) return;

      placeholderElements.forEach((el) => {
        const id = el.getAttribute("data-shortcode-id");
        const configEncoded = el.getAttribute("data-shortcode-config");

        if (!id || !configEncoded) return;

        // Skip if already rendered
        if (roots.has(id)) return;

        try {
          const data = JSON.parse(decodeURIComponent(configEncoded)) as ShortcodeData;

          // Create a React root and render the component
          const root = createRoot(el);
          roots.set(id, root);

          switch (data.type) {
            case "carousel":
              root.render(<CarouselShortcode data={data as CarouselShortcodeData} />);
              break;
          }
        } catch (e) {
          console.error("[ShortcodeRenderer] Failed to parse/render shortcode:", e);
        }
      });
    }, 100);

    // Cleanup function
    return () => {
      clearTimeout(timer);
      // Unmount all roots on cleanup
      roots.forEach((root) => {
        root.unmount();
      });
      roots.clear();
    };
  }, [html]);

  if (!content) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
